'use client';

import React, { useContext, useEffect, useMemo, useRef } from 'react';
import makeCancellable from 'make-cancellable-promise';
import clsx from 'clsx';
import invariant from 'tiny-invariant';
import warning from 'tiny-warning';
import * as pdfjs from 'pdfjs-dist';
// eslint-disable-next-line import/no-unresolved
import { XfaLayerParameters } from 'pdfjs-dist/types/src/display/xfa_layer';

import DocumentContext from './DocumentContext';
import PageContext from './PageContext';
import useResolver from './shared/hooks/useResolver';
import { cancelRunningTask } from './shared/utils';

import type { XFAForm } from './shared/types';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function XFALayer() {
    const documentContext = useContext(DocumentContext);
    const pageContext = useContext(PageContext);

    invariant(pageContext, 'Unable to find Page context.');

    const mergedProps = { ...documentContext, ...pageContext };
    const {
        imageResourcesPath,
        linkService,
        onGetXFAFormError: onGetXFAFormErrorProps,
        onGetXFAFormSuccess: onGetXFAFormSuccessProps,
        onRenderXFAFormLayerError: onRenderXFAFormLayerErrorProps,
        onRenderXFAFormLayerSuccess: onRenderXFAFormLayerSuccessProps,
        page,
        pdf,
        renderForms,
        rotate,
        scale = 1,
    } = mergedProps;

    invariant(
        pdf,
        'Attempted to load page annotations, but no document was specified. Wrap <Page /> in a <Document /> or pass explicit `pdf` prop.'
    );
    invariant(
        page,
        'Attempted to load page annotations, but no page was specified.'
    );
    invariant(
        linkService,
        'Attempted to load page annotations, but no linkService was specified.'
    );

    const [XFAState, XFADispatch] = useResolver<XFAForm>();
    const { value: XFAForm, error: XFAError } = XFAState;
    const layerElement = useRef<HTMLDivElement>(null);

    warning(
        parseInt(
            window
                .getComputedStyle(document.body)
                .getPropertyValue('--react-pdf-annotation-layer'),
            10
        ) === 1,
        'AnnotationLayer styles not found. Read more: https://github.com/wojtekmaj/react-pdf#support-for-annotations'
    );

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function onLoadSuccess() {
        if (!XFAForm) {
            // Impossible, but TypeScript doesn't know that
            return;
        }

        if (onGetXFAFormSuccessProps) {
            onGetXFAFormSuccessProps(XFAForm);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function onLoadError() {
        if (!XFAError) {
            // Impossible, but TypeScript doesn't know that
            return;
        }

        warning(false, XFAError.toString());

        if (onGetXFAFormErrorProps) {
            onGetXFAFormErrorProps(XFAError);
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function resetForms() {
        XFADispatch({ type: 'RESET' });
    }

    useEffect(resetForms, [XFADispatch, page]);

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function loadXFAForm() {
        if (!page) {
            return;
        }

        const cancellable = makeCancellable(page.getXfa());
        const runningTask = cancellable;

        cancellable.promise
            .then((nextXFA) => {
                if (nextXFA) {
                    console.log('test xfa', nextXFA);
                    XFADispatch({ type: 'RESOLVE', value: nextXFA });
                } else {
                    XFADispatch({
                        type: 'REJECT',
                        error: new Error('No XFA Found'),
                    });
                }
            })
            .catch((error) => {
                XFADispatch({ type: 'REJECT', error });
            });

        // eslint-disable-next-line consistent-return
        return () => {
            cancelRunningTask(runningTask);
        };
    }

    useEffect(loadXFAForm, [XFADispatch, page, renderForms]);

    useEffect(
        () => {
            if (XFAForm === undefined) {
                return;
            }

            if (XFAForm === false) {
                onLoadError();
                return;
            }

            onLoadSuccess();
        },
        // Ommitted callbacks so they are not called every time they change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [XFAForm]
    );

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function onRenderSuccess() {
        if (onRenderXFAFormLayerSuccessProps) {
            onRenderXFAFormLayerSuccessProps();
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function onRenderError(error: unknown) {
        warning(false, `${error}`);

        if (onRenderXFAFormLayerErrorProps) {
            onRenderXFAFormLayerErrorProps(error);
        }
    }

    const viewport = useMemo(
        () => page.getViewport({ scale, rotation: rotate }),
        [page, rotate, scale]
    );

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function renderXFALayer() {
        if (!pdf || !page || !linkService || !XFAForm) {
            return;
        }

        const { current: layer } = layerElement;

        if (!layer) {
            return;
        }

        const clonedViewport = viewport.clone({ dontFlip: true });

        const renderParameters: XfaLayerParameters = {
            xfaHtml: XFAForm,
            annotationStorage: pdf.annotationStorage,
            div: layer,
            // See https://github.com/mozilla/pdf.js/issues/17029
            // downloadManager: null as unknown as IDownloadManager,
            // imageResourcesPath,
            linkService,
            // page,
            // renderForms,
            viewport: clonedViewport,
        };

        layer.innerHTML = '';

        try {
            pdfjs.XfaLayer.render(renderParameters);
            // new pdfjs.AnnotationLayer(annotationLayerParameters).render(
            //   renderParameters
            // );

            // Intentional immediate callback
            onRenderSuccess();
        } catch (error) {
            onRenderError(error);
        }

        // eslint-disable-next-line consistent-return
        return () => {
            // TODO: Cancel running task?
        };
    }

    useEffect(
        renderXFALayer,
        // Ommitted callbacks so they are not called every time they change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [XFAForm, imageResourcesPath, linkService, page, renderForms, viewport]
    );

    return (
        <div
            className={clsx('react-pdf__Page__XFAform', 'formLayer')}
            ref={layerElement}
        />
    );
}
